import * as THREE from "three";
import gsap from "gsap";
import { EnergyLine } from "./EnergyLine.js";

export class Tutorial {
  constructor(game, grid, uiManager) {
    if (!game || !grid || !uiManager) {
      throw new Error(
        "Tutorial requires game, grid, and uiManager to be initialized"
      );
    }

    this.game = game;
    this.grid = grid;
    this.uiManager = uiManager;
    this.handIcon = null;
    this.tutorialStep = 0;
    this.isActive = false;
    this.createHandIcon();

    // Store the original input handler enabled state
    this.originalInputHandlerState = this.game.inputHandler
      ? this.game.inputHandler.enabled
      : false;
  }

  createHandIcon() {
    // Create a div for the hand icon
    this.handIcon = document.createElement("div");
    this.handIcon.style.cssText = `
      position: fixed;
      width: 40px;
      height: 40px;
      background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white"><path d="M9 11.24V7.5C9 6.12 10.12 5 11.5 5S14 6.12 14 7.5v3.74c1.21-.81 2-2.18 2-3.74C16 5.01 13.99 3 11.5 3S7 5.01 7 7.5c0 1.56.79 2.93 2 3.74zm9.84 4.63l-4.54-2.26c-.17-.07-.35-.11-.54-.11H13v-6c0-.83-.67-1.5-1.5-1.5S10 6.67 10 7.5v10.74c-3.6-.76-3.54-.75-3.67-.75-.31 0-.59.13-.79.33l-.79.8 4.94 4.94c.27.27.65.44 1.06.44h6.79c.75 0 1.33-.55 1.44-1.28l.75-5.27c.01-.07.02-.14.02-.2 0-.62-.38-1.16-.91-1.38z"/></svg>');
      background-size: contain;
      background-repeat: no-repeat;
      pointer-events: none;
      opacity: 0;
      transition: all 0.3s ease;
      z-index: 1000;
      filter: drop-shadow(0 0 5px rgba(255,255,255,0.5));
    `;
    document.body.appendChild(this.handIcon);
  }

  async start() {
    try {
      this.isActive = true;
      this.tutorialStep = 0;

      // Disable input handler during tutorial
      if (this.game && this.game.inputHandler) {
        this.originalInputHandlerState = this.game.inputHandler.enabled;
        this.game.inputHandler.enabled = false;
      }

      // Add a small delay to allow the title screen to fade out completely
      await this.delay(600);

      // Highlight UI elements and show game goals message
      await this.showTutorialMessage(
        "Game Goals",
        "Collect energy, clear negative crystals and connect the chakra colors to progress.",
        "Show me how",
        {
          position: "center",
          highlightSelectors: [
            ".desktop-panel .stats-row",
            ".mobile-panel .stats-row",
            ".mobile-panel .connection-row",
          ],
        }
      );

      // Ensure grid is ready before demonstration
      if (!this.grid || !this.grid.setupTutorialLayout) {
        throw new Error("Grid not properly initialized for tutorial");
      }

      // Demonstrate crystal connection
      await this.demonstrateConnection();

      // Show final message
      await this.showTutorialMessage(
        "You're Ready!",
        "Begin your journey to restore balance to the chakras.",
        "Begin",
        { position: "center" }
      );

      this.complete();
    } catch (error) {
      console.error("Error in tutorial sequence:", error);
      this.complete();
    }
  }

  async showTutorialMessage(title, message, buttonText, options = {}) {
    return new Promise((resolve) => {
      // Create a backdrop for smooth transitions
      const backdrop = document.createElement("div");
      backdrop.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        z-index: 1997;
        opacity: 0;
        transition: opacity 0.5s ease;
      `;
      document.body.appendChild(backdrop);

      // Fade in the backdrop
      setTimeout(() => {
        backdrop.style.opacity = "1";
      }, 10);

      // Create highlight overlays if needed
      let highlightOverlays = [];
      if (options.highlightSelectors) {
        // First, boost the z-index of the top panel
        const topPanel = document.querySelector(".top-panel");
        const originalTopPanelZIndex = topPanel ? topPanel.style.zIndex : null;
        if (topPanel) {
          topPanel.style.zIndex = "2000";
          // Store restore function for top panel
          highlightOverlays.push(() => {
            topPanel.style.zIndex = originalTopPanelZIndex || "1000";
          });
        }

        // Create the dark background first
        const darkBg = document.createElement("div");
        darkBg.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.7);
          z-index: 1998;
          opacity: 0;
          transition: opacity 0.5s ease;
        `;
        document.body.appendChild(darkBg);
        highlightOverlays.push(darkBg);

        // Fade in the dark background
        setTimeout(() => {
          darkBg.style.opacity = "1";
        }, 10);

        // Create highlights for each element
        options.highlightSelectors.forEach((selector) => {
          const elements = document.querySelectorAll(selector);
          elements.forEach((element) => {
            const rect = element.getBoundingClientRect();

            // Create a hole in the dark background
            const hole = document.createElement("div");
            hole.style.cssText = `
              position: fixed;
              top: ${rect.top}px;
              left: ${rect.left}px;
              width: ${rect.width}px;
              height: ${rect.height}px;
              background: transparent;
              z-index: 1999;
              opacity: 0;
              transition: opacity 0.5s ease;
            `;
            document.body.appendChild(hole);
            highlightOverlays.push(hole);

            // Fade in the hole
            setTimeout(() => {
              hole.style.opacity = "1";
            }, 10);

            // Create the golden border
            const highlight = document.createElement("div");
            highlight.style.cssText = `
              position: fixed;
              top: ${rect.top}px;
              left: ${rect.left}px;
              width: ${rect.width}px;
              height: ${rect.height}px;
              border: 2px solid #FFD700;
              border-radius: 8px;
              box-shadow: 0 0 10px rgba(255, 215, 0, 0.5);
              animation: pulse 2s infinite;
              z-index: 2001;
              pointer-events: none;
              opacity: 0;
              transition: opacity 0.5s ease;
            `;
            document.body.appendChild(highlight);
            highlightOverlays.push(highlight);

            // Fade in the highlight
            setTimeout(() => {
              highlight.style.opacity = "1";
            }, 10);

            // Store original styles
            const originalStyles = {
              zIndex: element.style.zIndex,
              background: element.style.background,
              backdropFilter: element.style.backdropFilter,
            };

            // Modify the element's styles to ensure visibility
            element.style.zIndex = "2000";
            element.style.background = "rgba(0, 0, 0, 0.5)";
            element.style.backdropFilter = "blur(5px)";

            // Store the restore function
            highlightOverlays.push(() => {
              element.style.zIndex = originalStyles.zIndex;
              element.style.background = originalStyles.background;
              element.style.backdropFilter = originalStyles.backdropFilter;
            });
          });
        });
      }

      const dialog = document.createElement("div");

      // Always use center positioning for all dialogs
      let dialogStyle = `
        position: fixed;
        background: rgba(0, 0, 0, 0.9);
        padding: 30px;
        border-radius: 15px;
        color: white;
        text-align: center;
        z-index: 2002;
        max-width: 80%;
        font-family: "Philosopher", sans-serif;
        box-shadow: 0 0 20px rgba(255, 215, 0, 0.2);
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) scale(0.95);
        opacity: 0;
        transition: opacity 0.5s ease, transform 0.5s ease;
        width: 300px;
      `;

      // Set a fixed width for better consistency
      if (window.innerWidth <= 768) {
        // Mobile devices
        dialogStyle += `
          width: 90%;
          max-width: 90%;
        `;
      }

      dialog.style.cssText = dialogStyle;

      const titleElement = document.createElement("h2");
      titleElement.textContent = title;
      titleElement.style.cssText = `
        margin-bottom: 15px;
        font-family: "Philosopher", sans-serif;
        font-size: 24px;
        color: #FFD700;
        font-weight: bold;
        text-shadow: 0 0 10px rgba(255, 215, 0, 0.3);
      `;

      const messageElement = document.createElement("p");
      messageElement.textContent = message;
      messageElement.style.cssText = `
        margin-bottom: 25px;
        font-family: "Philosopher", sans-serif;
        font-size: 16px;
        line-height: 1.5;
        text-shadow: 0 0 10px rgba(255, 255, 255, 0.2);
      `;

      const button = document.createElement("button");
      button.textContent = buttonText;
      button.style.cssText = `
        padding: 10px 30px;
        border: none;
        border-radius: 25px;
        background: #FFD700;
        color: black;
        font-size: 16px;
        cursor: pointer;
        transition: all 0.3s ease;
        font-family: "Philosopher", sans-serif;
        font-weight: bold;
        box-shadow: 0 0 15px rgba(255, 215, 0, 0.3);
      `;

      button.onmouseover = () => {
        button.style.transform = "scale(1.1)";
        button.style.boxShadow = "0 0 20px rgba(255, 215, 0, 0.5)";
      };

      button.onmouseout = () => {
        button.style.transform = "scale(1)";
        button.style.boxShadow = "0 0 15px rgba(255, 215, 0, 0.3)";
      };

      button.onclick = () => {
        // Fade out dialog
        dialog.style.opacity = "0";
        dialog.style.transform = "translate(-50%, -50%) scale(0.95)";

        // Fade out all overlays
        highlightOverlays.forEach((overlay) => {
          if (typeof overlay === "function") {
            overlay(); // Restore original styles
          } else {
            overlay.style.opacity = "0";
          }
        });

        // Fade out backdrop
        backdrop.style.opacity = "0";

        // Remove elements after animation completes
        setTimeout(() => {
          document.body.removeChild(dialog);

          highlightOverlays.forEach((overlay) => {
            if (typeof overlay !== "function" && overlay.parentNode) {
              overlay.parentNode.removeChild(overlay);
            }
          });

          document.body.removeChild(backdrop);
          resolve();
        }, 500);
      };

      dialog.appendChild(titleElement);
      dialog.appendChild(messageElement);
      dialog.appendChild(button);
      document.body.appendChild(dialog);

      // Update animation styles
      if (!document.querySelector("#tutorial-animations")) {
        const style = document.createElement("style");
        style.id = "tutorial-animations";
        style.textContent = `
          @keyframes pulse {
            0% { box-shadow: 0 0 10px rgba(255, 215, 0, 0.5); }
            50% { box-shadow: 0 0 20px rgba(255, 215, 0, 0.8); }
            100% { box-shadow: 0 0 10px rgba(255, 215, 0, 0.5); }
          }
        `;
        document.head.appendChild(style);
      }

      // Fade in the dialog with a slight scale animation
      setTimeout(() => {
        dialog.style.opacity = "1";
        dialog.style.transform = "translate(-50%, -50%) scale(1)";
      }, 10);
    });
  }

  async demonstrateConnection() {
    try {
      if (!this.grid) {
        throw new Error("Grid not available for tutorial demonstration");
      }

      // Create a predetermined tutorial board layout
      await this.setupTutorialBoard();

      // Wait a bit for the board to be ready
      await this.delay(500);

      // Get crystal positions
      const startCrystal = this.grid.getTutorialStartCrystal();
      const midCrystal = this.grid.crystals[3][4]; // negative crystal
      const endCrystal = this.grid.getTutorialEndCrystal();

      if (!startCrystal || !midCrystal || !endCrystal) {
        throw new Error("Tutorial crystals not properly initialized");
      }

      // Get screen positions
      const startPos = this.getScreenPosition(startCrystal.mesh.position);
      const midPos = this.getScreenPosition(midCrystal.mesh.position);
      const endPos = this.getScreenPosition(endCrystal.mesh.position);

      // Show hand at start position
      this.handIcon.style.opacity = "1";
      this.handIcon.style.left = startPos.x + "px";
      this.handIcon.style.top = startPos.y + "px";

      // Add small delay before starting movement
      await this.delay(500);

      // Create energy line instances
      const energyLine = new EnergyLine({
        color: new THREE.Color(startCrystal.color),
        intensity: 1.2,
        radius0: 0.1,
        radius1: 0.1,
        maxIterations: 20,
        straightness: 0.8,
      });

      // Initialize line points with start crystal position
      const linePoints = [
        new THREE.Vector3(
          startCrystal.mesh.position.x,
          0.5,
          startCrystal.mesh.position.z
        ),
        new THREE.Vector3(
          startCrystal.mesh.position.x,
          0.5,
          startCrystal.mesh.position.z
        ),
      ];

      // Generate initial path
      const mesh = energyLine.generatePath(linePoints);
      if (mesh) {
        this.grid.scene.add(mesh);
      }

      // Play tap sound
      if (this.grid.soundManager) {
        this.grid.soundManager.playTap();
      }

      // Animate hand to middle crystal with intermediate points
      const startToMidSteps = 20; // Number of steps for smoother animation
      this.handIcon.style.transition = "all 0.05s linear"; // Faster transitions for smoother movement

      // Create intermediate points between start and mid crystal
      for (let i = 1; i <= startToMidSteps; i++) {
        const progress = i / startToMidSteps;

        // Calculate intermediate screen position
        const intermediateScreenX =
          startPos.x + (midPos.x - startPos.x) * progress;
        const intermediateScreenY =
          startPos.y + (midPos.y - startPos.y) * progress;

        // Move hand to intermediate position
        this.handIcon.style.left = intermediateScreenX + "px";
        this.handIcon.style.top = intermediateScreenY + "px";

        // Calculate corresponding world position
        const worldX =
          startCrystal.mesh.position.x +
          (midCrystal.mesh.position.x - startCrystal.mesh.position.x) *
            progress;
        const worldZ =
          startCrystal.mesh.position.z +
          (midCrystal.mesh.position.z - startCrystal.mesh.position.z) *
            progress;

        // Update the last point in the line
        linePoints[linePoints.length - 1] = new THREE.Vector3(
          worldX,
          0.5,
          worldZ
        );

        // Update energy line
        energyLine.generatePath(linePoints);

        // Update animation
        energyLine.update(Date.now() * 0.001);

        // Wait a short time between steps
        await this.delay(50);
      }

      // Add mid crystal to the path
      linePoints[linePoints.length - 1] = new THREE.Vector3(
        midCrystal.mesh.position.x,
        0.5,
        midCrystal.mesh.position.z
      );
      linePoints.push(
        new THREE.Vector3(
          midCrystal.mesh.position.x,
          0.5,
          midCrystal.mesh.position.z
        )
      );

      // Update energy line
      energyLine.generatePath(linePoints);

      // Play connection sound
      if (this.grid.soundManager) {
        this.grid.soundManager.playConnection();
      }

      // Increase intensity for the second segment
      energyLine.setIntensity(1.5);

      // Wait a moment at the middle crystal
      await this.delay(300);

      // Animate hand to end crystal with intermediate points
      const midToEndSteps = 20;

      // Create intermediate points between mid and end crystal
      for (let i = 1; i <= midToEndSteps; i++) {
        const progress = i / midToEndSteps;

        // Calculate intermediate screen position
        const intermediateScreenX = midPos.x + (endPos.x - midPos.x) * progress;
        const intermediateScreenY = midPos.y + (endPos.y - midPos.y) * progress;

        // Move hand to intermediate position
        this.handIcon.style.left = intermediateScreenX + "px";
        this.handIcon.style.top = intermediateScreenY + "px";

        // Calculate corresponding world position
        const worldX =
          midCrystal.mesh.position.x +
          (endCrystal.mesh.position.x - midCrystal.mesh.position.x) * progress;
        const worldZ =
          midCrystal.mesh.position.z +
          (endCrystal.mesh.position.z - midCrystal.mesh.position.z) * progress;

        // Update the last point in the line
        linePoints[linePoints.length - 1] = new THREE.Vector3(
          worldX,
          0.5,
          worldZ
        );

        // Update energy line
        energyLine.generatePath(linePoints);

        // Update animation
        energyLine.update(Date.now() * 0.001);

        // Wait a short time between steps
        await this.delay(50);
      }

      // Add end crystal to the path
      linePoints[linePoints.length - 1] = new THREE.Vector3(
        endCrystal.mesh.position.x,
        0.5,
        endCrystal.mesh.position.z
      );

      // Update energy line
      energyLine.generatePath(linePoints);

      // Play second connection sound with increased pitch
      if (this.grid.soundManager) {
        this.grid.soundManager.currentPathLength++;
        this.grid.soundManager.playConnection();
      }

      // Increase intensity for the completed path
      energyLine.setIntensity(1.8);

      // Hide hand
      this.handIcon.style.opacity = "0";

      // Continue animating the energy line for a moment
      const startTime = Date.now();
      const duration = 800; // Animation time after hand movement

      await new Promise((resolve) => {
        const animate = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const time = elapsed * 0.001;

          energyLine.update(time);

          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            // Clean up energy line
            if (energyLine) {
              energyLine.dispose();
              if (energyLine.mesh) this.grid.scene.remove(energyLine.mesh);
            }
            resolve();
          }
        };
        animate();
      });

      // Play connection complete sound
      if (this.grid.soundManager) {
        this.grid.soundManager.playConnected();
        // Reset path length counter
        this.grid.soundManager.resetPathLength();
      }

      // Clear the connected crystals but don't spawn new ones
      const path = [startCrystal, midCrystal, endCrystal];
      for (const crystal of path) {
        if (crystal && crystal.mesh) {
          this.grid.createRemovalEffect(
            crystal.mesh.position.clone(),
            crystal.color || 0xffffff
          );
          this.grid.scene.remove(crystal.mesh);
        }
      }

      // Wait a bit before proceeding
      await this.delay(500);
    } catch (error) {
      console.error("Error in tutorial demonstration:", error);
      throw error;
    }
  }

  async setupTutorialBoard() {
    try {
      // Ensure the grid is initialized
      if (!this.grid) {
        console.error("Grid not properly initialized");
        return;
      }

      // Setup the tutorial layout
      await this.grid.setupTutorialLayout();

      // Store references to the tutorial crystals
      this.tutorialStartCrystal = this.grid.getTutorialStartCrystal();
      this.tutorialEndCrystal = this.grid.getTutorialEndCrystal();

      if (!this.tutorialStartCrystal || !this.tutorialEndCrystal) {
        console.error("Tutorial crystals not created properly");
      }
    } catch (error) {
      console.error("Error setting up tutorial board:", error);
    }
  }

  getScreenPosition(position) {
    const vector = position.clone();
    vector.project(this.game.camera);

    return {
      x: ((vector.x + 1) * window.innerWidth) / 2,
      y: ((-vector.y + 1) * window.innerHeight) / 2,
    };
  }

  async animateConnection() {
    // Trigger the connection animation
    // This will be handled by the existing game logic
    await this.grid.playTutorialConnection();
  }

  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async complete() {
    try {
      this.isActive = false;
      if (this.handIcon && this.handIcon.parentNode) {
        // Fade out the hand icon
        this.handIcon.style.transition = "opacity 0.5s ease";
        this.handIcon.style.opacity = "0";

        // Remove after animation completes
        setTimeout(() => {
          if (this.handIcon && this.handIcon.parentNode) {
            this.handIcon.parentNode.removeChild(this.handIcon);
          }
        }, 500);
      }

      // Start the game with a smooth transition
      if (this.game && typeof this.game.startNewGame === "function") {
        // Generate the initial game layout
        const initialLayout = this.grid.generateInitialLayout();

        // Start camera transition using our custom controller
        this.cameraController.transitionToGameView();

        // Transition crystals while camera is moving
        await this.grid.transitionToGameLayout(initialLayout);

        // Start the game - but don't let it do another camera transition or generate new gems
        // Pass true to indicate we're coming from tutorial and pass the layout we already transitioned to
        this.game.startNewGame(true, initialLayout);

        // Note: We don't need to explicitly re-enable the input handler here
        // because startNewGame() already sets inputHandler.enabled = true
      } else {
        console.error("Game instance or startNewGame method not available");
      }
    } catch (error) {
      console.error("Error completing tutorial:", error);

      // In case of error, ensure input handler is re-enabled if game exists
      if (this.game && this.game.inputHandler) {
        this.game.inputHandler.enabled = true;
      }
    }
  }

  showGameGoalsDialog() {
    // Get the stats row element based on device type
    const isMobile = window.innerWidth <= 768;
    const statsRow = isMobile
      ? document.querySelector(".mobile-panel .stats-row")
      : document.querySelector(".desktop-panel .stats-row");

    if (!statsRow) {
      console.error("Stats row not found");
      return;
    }

    const rect = statsRow.getBoundingClientRect();
    const dialogWidth = 300; // Approximate width of dialog

    // Calculate position based on device type
    let left;
    if (isMobile) {
      // Center the dialog horizontally on mobile
      left = (window.innerWidth - dialogWidth) / 2;
    } else {
      // Position relative to stats row on desktop, but ensure it's centered under it
      left = rect.left + (rect.width - dialogWidth) / 2;
    }

    const dialog = document.createElement("div");
    dialog.className = "tutorial-dialog";
    dialog.style.position = "absolute";
    dialog.style.left = `${left}px`;
    dialog.style.top = `${rect.bottom + 40}px`;
    dialog.style.width = `${dialogWidth}px`;
    dialog.style.transform = "translateY(20px)";
    dialog.style.opacity = "0";
    dialog.style.transition = "opacity 0.5s ease-out, transform 0.5s ease-out";

    // ... existing code ...
  }

  // Add a camera controller implementation to make the transition smoother
  get cameraController() {
    // Create a camera controller object if it doesn't exist
    if (!this._cameraController) {
      this._cameraController = {
        transitionToGameView: () => {
          if (!this.game || !this.game.camera) return;

          // Get current camera position
          const startPosition = this.game.camera.position.clone();

          // Calculate target position (overhead view)
          const width = window.innerWidth;
          const height = window.innerHeight;
          const aspect = width / height;
          const isPortrait = height > width;

          // Calculate the required camera height to maintain consistent board size
          const hitAreaWorldSize = 7 * 1.2 + 0.6; // Grid size plus padding

          // Adjust FOV based on screen size and UI space
          const topUIHeight = isPortrait ? 160 : 60;
          const bottomUIHeight = 80;
          const availableHeight = height - topUIHeight - bottomUIHeight;
          const availableAspect = width / availableHeight;

          // Calculate FOV to fit the board in the available space
          const targetFov = isPortrait ? 75 : 70;
          const adjustedFov =
            2 *
            Math.atan(
              Math.tan((targetFov * Math.PI) / 180 / 2) *
                (height / availableHeight)
            ) *
            (180 / Math.PI);

          // Calculate distances needed for both vertical and horizontal constraints
          const vFov = (adjustedFov * Math.PI) / 180; // vertical FOV in radians
          const hFov = 2 * Math.atan(Math.tan(vFov / 2) * availableAspect); // horizontal FOV in radians

          // Calculate distance needed for vertical fit
          const distanceVertical = hitAreaWorldSize / 2 / Math.tan(vFov / 2);
          // Calculate distance needed for horizontal fit
          const distanceHorizontal = hitAreaWorldSize / 2 / Math.tan(hFov / 2);

          // Use the larger distance to ensure hit areas fit in both dimensions
          const distance = Math.max(distanceVertical, distanceHorizontal) * 1.1; // Add 10% margin

          // Set target position (overhead view)
          const targetPosition = new THREE.Vector3(0, distance, 0);

          // Use GSAP for smooth animation
          const duration = 1.5; // 1.5 seconds for a smoother transition

          // Animate camera position
          gsap.to(this.game.camera.position, {
            x: targetPosition.x,
            y: targetPosition.y,
            z: targetPosition.z,
            duration: duration,
            ease: "power2.inOut",
            onUpdate: () => {
              this.game.camera.lookAt(0, 0, 0);
            },
          });

          // Also animate FOV for smoother transition
          gsap.to(this.game.camera, {
            fov: adjustedFov,
            duration: duration,
            ease: "power2.inOut",
            onUpdate: () => {
              this.game.camera.updateProjectionMatrix();
            },
          });

          // Set a flag to indicate that camera transition has already occurred
          // This will be used to prevent the second transition in startNewGame
          this.game.cameraTransitionComplete = true;
        },
      };
    }

    return this._cameraController;
  }
}
